import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { initiateEwayPayment, checkEwayPaymentStatus, formatPrice } from '../services/bookingService';
import { parseISO, format } from 'date-fns';

/**
 * eWay Payment Form component
 * @param {Object} props Component props
 * @param {number} props.clinicId Clinic ID
 * @param {number} props.serviceId Service/appointment type ID
 * @param {string} props.practitionerId Practitioner ID
 * @param {number} props.amount Payment amount
 * @param {string} props.patientEmail Patient email address
 * @param {string} props.patientName Patient full name
 * @param {Object} props.appointmentDetails Additional appointment details
 * @param {Function} props.onSuccess Success callback
 * @param {Function} props.onCancel Cancel callback
 * @param {string} props.returnUrl Optional return URL override
 * @param {boolean} props.loading Initial loading state
 */
const EwayPaymentForm = ({
  clinicId,
  serviceId,
  practitionerId,
  amount,
  patientEmail,
  patientName,
  appointmentDetails,
  onSuccess,
  onCancel,
  returnUrl,
  loading: initialLoading = false
}) => {
  const [loading, setLoading] = useState(initialLoading);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [paymentWindow, setPaymentWindow] = useState(null);
  const [accessCode, setAccessCode] = useState(null);
  const [error, setError] = useState(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);

  // Extract clinic stub from URL
  const getClinicStub = () => {
    const pathParts = window.location.pathname.split('/');
    return pathParts[1] || ''; // The first segment after the domain
  };

  // Save booking data to localStorage for continuity after redirect
  const saveBookingData = () => {
    try {
      // Extract the clinic stub
      const clinicStub = getClinicStub();
      
      // Save minimal booking info to localStorage
      localStorage.setItem('currentBooking', JSON.stringify({
        clinicId,
        clinicStub,
        serviceId,
        practitionerId,
        accessCode
      }));
      
      // Get the full booking data from the parent component
      const bookingSessionData = window.bookingData || {};
      
      if (Object.keys(bookingSessionData).length === 0) {
        // If window.bookingData is unavailable, construct from our props
        const bookingData = {
          service: { id: serviceId },
          practitioner: { id: practitionerId },
          appointmentTime: appointmentDetails?.starts_at ? {
            iso: appointmentDetails.starts_at
          } : null,
          patient: {
            first_name: appointmentDetails?.patient_first_name || '',
            last_name: appointmentDetails?.patient_last_name || '',
            email: patientEmail || '',
            phone: appointmentDetails?.patient_phone || ''
          },
          notes: appointmentDetails?.notes || '',
          payment: {
            status: 'pending',
            processor: 'eway',
            accessCode
          }
        };
        
        // Save to sessionStorage
        sessionStorage.setItem('bookingSession', JSON.stringify(bookingData));
      } else {
        const updatedData = {
          ...bookingSessionData,
          payment: {
            ...bookingSessionData.payment,
            status: 'pending',
            processor: 'eway',
            accessCode
          }
        };
        
        sessionStorage.setItem('bookingSession', JSON.stringify(updatedData));
      }
    } catch (e) {
      console.error('Error saving booking data to storage:', e);
    }
  };

  // Initiate the eWay payment
  const initiatePayment = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check for required fields
      if (!clinicId || !amount || !patientEmail || !patientName) {
        throw new Error('Missing required payment information');
      }
      
      // Get clinic stub for return URLs
      const clinicStub = getClinicStub();
      
      // Prepare payment data
      const paymentData = {
        clinic_id: clinicId,
        clinic_stub: clinicStub,
        amount,
        patient_email: patientEmail,
        patient_name: patientName,
        appointment_type_id: serviceId,
        practitioner_id: practitionerId,
        returnUrl,
        // Include appointment details
        ...appointmentDetails
      };
      
      // Initiate payment with eWay
      const response = await initiateEwayPayment(paymentData);
      
      if (!response || !response.success) {
        throw new Error(response?.error || 'Failed to initiate payment');
      }
      
      // Save access code for status checking
      setAccessCode(response.accessCode);
      setPaymentUrl(response.formUrl);
      
      // Save payment data to localStorage for after redirect
      saveBookingData();
      
      // Using eWay Direct Connection method
      console.log('Setting up eWay Direct Connection payment with AccessCode:', response.accessCode);
      
      if (response.formActionUrl) {
        console.log('Received Eway SharedPaymentUrl:', response.formActionUrl);
        
        // Using the SharedPaymentUrl which requires direct redirection, not a form submission
        console.log('Redirecting to SharedPaymentUrl - this directly shows the payment form');
        
        // Log what we're doing
        console.log(`Redirecting to: ${response.formActionUrl}`);
        console.log(`For AccessCode: ${response.accessCode}`);
        
        // Directly redirect to the SharedPaymentUrl
        window.location.href = response.formActionUrl;
        
        // Start checking payment status periodically
        const interval = setInterval(() => {
          checkPaymentStatus(response.accessCode);
        }, 5000);
        
        setStatusCheckInterval(interval);
      
      } else {
        console.error('No formActionUrl provided in response');
        setError('Payment gateway URL not provided');
        setLoading(false);
      }
      
      // Display message for debugging
      console.log('Redirecting to eWay payment page');
      
      // Start checking payment status periodically
      const interval = setInterval(() => {
        checkPaymentStatus(response.accessCode);
      }, 5000);
      
      setStatusCheckInterval(interval);
      
    } catch (err) {
      console.error('Error initiating eWay payment:', err);
      setError(err.message || 'An error occurred while setting up payment');
      setLoading(false);
    }
  };
  
  // Check the status of an existing payment
  const checkPaymentStatus = async (code) => {
    try {
      // Only check if we have a clinic ID and an access code
      if (!clinicId || !code) return;
      
      const response = await checkEwayPaymentStatus(code, clinicId);
      
      // If payment is complete, handle success
      if (response.status === 'complete') {
        clearInterval(statusCheckInterval);
        
        // Check if the backend already created the appointment
        if (response.appointment_created) {
          console.log('Appointment was automatically created by backend:', response.appointment_id);
          
          // Instead of showing the confirmation page, redirect directly to a success page
          // Extract clinic stub from the URL if available
          const pathParts = window.location.pathname.split('/');
          const clinicStub = pathParts[1] || '';
          
          // Create success URL with appointment ID and transaction ID
          const successUrl = `/${clinicStub}/booking/success?appointment_id=${response.appointment_id}&transaction_id=${response.transactionId}`;
          
          // Show alert if needed
          alert('Your appointment has been successfully booked! You will receive a confirmation email shortly.');
          
          // Redirect
          window.location.href = successUrl;
          return;
        }
        
        // Otherwise, proceed with normal success handling
        handlePaymentSuccess(response);
      } else if (response.status === 'failed') {
        clearInterval(statusCheckInterval);
        handlePaymentFailure(response);
      }
      
    } catch (err) {
      console.error('Error checking payment status:', err);
      // Don't set error here as this is a background check
    }
  };
  
  // Handle successful payment
  const handlePaymentSuccess = (paymentResponse) => {
    setLoading(false);
    // Close payment window if it's still open
    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.close();
    }
    
    // Check if the payment has already created an appointment
    if (paymentResponse && paymentResponse.appointment_created) {
      console.log('Appointment was automatically created after payment:', paymentResponse.appointment_id);
    } else {
      console.log('No appointment was automatically created, trying to create one now...');
      // If the appointment wasn't created automatically, we'll create it here
      try {
        // Get booking data from session storage
        const bookingData = JSON.parse(sessionStorage.getItem('bookingSession') || '{}');
        
        // Check if we have necessary booking data
        if (bookingData && bookingData.service && bookingData.practitioner && bookingData.appointmentTime) {
          // Build appointment creation request
          const appointmentData = {
            clinic_id: clinicId,
            appointment_type_id: bookingData.service.id,
            practitioner_id: bookingData.practitioner.id,
            starts_at: bookingData.appointmentTime.iso,
            ends_at: bookingData.appointmentTime.ends_at || null, // May need to calculate this
            patient_first_name: bookingData.patient.first_name,
            patient_last_name: bookingData.patient.last_name,
            patient_email: bookingData.patient.email,
            patient_phone: bookingData.patient.phone,
            notes: bookingData.notes || '',
            payment_id: paymentResponse.transactionId || accessCode
          };
          
          // Call API to create booking
          import('../services/bookingService').then(module => {
            const { createBooking } = module;
            createBooking(appointmentData)
              .then(response => {
                console.log('Successfully created appointment automatically:', response);
                if (onSuccess) {
                  // Include appointment info in the response
                  const updatedResponse = {
                    ...paymentResponse,
                    appointment_created: true,
                    appointment_id: response.appointment?.id
                  };
                  onSuccess(updatedResponse);
                }
              })
              .catch(error => {
                console.error('Error automatically creating appointment:', error);
                // Still call onSuccess even if creating appointment fails
                if (onSuccess) {
                  onSuccess(paymentResponse);
                }
              });
          });
          return; // Exit early since we're handling callback in async code
        } else {
          console.warn('Insufficient booking data to create appointment automatically');
        }
      } catch (error) {
        console.error('Error in automatic appointment creation:', error);
      }
    }
    
    // Default success handling when we don't have enough data or if appointment creation failed
    if (onSuccess) {
      onSuccess(paymentResponse);
    }
  };
  
  // Handle failed payment
  const handlePaymentFailure = (paymentResponse) => {
    setLoading(false);
    setError('Payment was not completed successfully');
    
    // Close payment window if it's still open
    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.close();
    }
  };
  
  // Listen for messages from the popup window
  useEffect(() => {
    const handleMessage = (event) => {
      // Check for payment complete messages
      if (event.data?.type === 'paymentComplete' && accessCode) {
        // The payment was completed in the popup, check status now
        checkPaymentStatus(accessCode);
      }
      // Handle payment cancelled messages
      else if (event.data === 'PAYMENT_CANCELLED') {
        clearInterval(statusCheckInterval);
        if (onCancel) onCancel();
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [accessCode, statusCheckInterval]);
  
  // Start payment process when component mounts
  useEffect(() => {
    // Check if this is a free appointment using a comprehensive approach
    // 1. First convert any string to a number if possible
    let numericAmount = amount;
    if (typeof amount === 'string') {
      numericAmount = parseFloat(amount);
      // If parsing failed (NaN), keep the original value
      if (isNaN(numericAmount)) {
        numericAmount = amount;
      }
    }
    
    // 2. Now do the actual comparison with the numeric value if possible
    const isFreeAppointment = 
      amount === null || 
      amount === 0 || 
      amount === '0' || 
      numericAmount === 0 ||
      numericAmount === 0.0;
    
    console.log('%c PAYMENT AMOUNT ANALYSIS', 'background: #ff8c00; color: black; font-size: 16px; padding: 5px;');
    console.log('- Original amount value:', amount);
    console.log('- Original amount type:', typeof amount);
    console.log('- Numeric amount after conversion:', numericAmount);
    console.log('- Is null check:', amount === null);
    console.log('- Is 0 check:', amount === 0);
    console.log('- Is "0" string check:', amount === '0');
    console.log('- Is numeric 0 check:', numericAmount === 0);
    console.log('- Final free appointment status:', isFreeAppointment);
    
    if (isFreeAppointment) {
      console.log('%c FREE APPOINTMENT DETECTED IN PAYMENT FORM - SKIPPING PAYMENT PROCESS', 'background: #00ff00; color: black; font-size: 16px; padding: 5px;');
      
      // Handle as a successful payment with a free-appointment transaction ID
      handlePaymentSuccess({
        status: 'complete',
        transactionId: 'free-appointment'
      });
      return;
    }
    
    // Log what we're doing with paid appointments
    console.log('Paid appointment detected in eWay form:');
    console.log('- Amount value:', amount);
    console.log('- Amount type:', typeof amount);
    console.log('- Initiating payment process...');
    
    initiatePayment();
    
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, []);
  
  // Parse appointment details for display
  const formatAppointmentTime = () => {
    if (!appointmentDetails || !appointmentDetails.starts_at) return 'Unknown time';
    
    const startTime = parseISO(appointmentDetails.starts_at);
    return format(startTime, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
  };
  
  // Render payment summary
  const renderPaymentSummary = () => {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Appointment Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Date & Time
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {formatAppointmentTime()}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Patient
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="body2">
                {patientName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {patientEmail}
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography variant="h6" color="primary">
                {formatPrice(amount)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Handle close/cancel button
  const handleClose = () => {
    if (paymentWindow && !paymentWindow.closed) {
      paymentWindow.close();
    }
    
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
    }
    
    if (onCancel) {
      onCancel();
    }
  };

  // Main render
  if (loading) {
    return (
      <Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 2,
          backgroundColor: 'purple',
          color: 'white',
          padding: '10px',
          borderRadius: '5px'
        }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={handleClose}
            sx={{ mr: 2, color: 'white', borderColor: 'white' }}
            variant="outlined"
            disabled={paymentUrl !== null}
          >
            Back
          </Button>
          <Typography variant="h6">
            DEV SERVER VERSION - PM2 Deployment - {new Date().toLocaleString()}
          </Typography>
        </Box>
        
        {renderPaymentSummary()}
        
        <Paper variant="outlined" sx={{ 
          p: 3, 
          textAlign: 'center',
          border: '4px solid red',
          backgroundColor: '#fff9f9'
        }}>
          {paymentUrl ? (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'red' }}>
                TEST MODE - Complete Your Payment
              </Typography>
              <img 
                src="https://www.eway.com.au/graphics/logo-on-light-lg.png"
                alt="eWAY logo"
                style={{ maxHeight: 40, margin: '1rem 0' }}
              />
              <Typography variant="body2" sx={{ mt: 2, mb: 3 }}>
                Please complete your payment in the newly opened window. 
                This page will update automatically when your payment is complete.
              </Typography>
              <CircularProgress size={30} sx={{ mt: 2, color: 'red' }} />
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Using BOTH parameter names (EWAY_ACCESSCODE and AccessCode)
              </Typography>
              <Button 
                variant="outlined"
                onClick={handleClose}
                sx={{ mt: 3, borderColor: 'red', color: 'red' }}
              >
                Cancel Payment
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'red' }}>
                TEST MODE - Preparing Payment...
              </Typography>
              <CircularProgress sx={{ mt: 2, color: 'red' }} />
            </Box>
          )}
        </Paper>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={handleClose}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h6">
            Payment
          </Typography>
        </Box>
        
        {renderPaymentSummary()}
        
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            variant="outlined"
            onClick={handleClose}
          >
            Back
          </Button>
          <Button 
            variant="contained"
            onClick={initiatePayment}
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }
  
  // This should rarely happen, but just in case
  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>
        Payment Processing
      </Typography>
      <CircularProgress />
    </Box>
  );
};

export default EwayPaymentForm;